import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { TokenService } from 'src/app/shared/service/token.service';
import { BreadcrumbService } from 'src/app/shared/service/breadcrumb.service';
import { SharedDataService } from 'src/app/shared/service/shared-data/shared-data.service';

import { MessagesService } from 'src/app/modules/messages/services/messages.service';

type OneSignalSdk = {
  init(options: Record<string, unknown>): Promise<void>;
  login(externalId: string): Promise<void>;
  logout(): Promise<void>;
  Notifications: {
    isPushSupported(): boolean;
    permission: boolean;
    addEventListener(
      eventName: 'click' | 'foregroundWillDisplay',
      listener: (event: any) => void
    ): void;
  };
  Slidedown: {
    promptPush(options?: { force?: boolean }): Promise<void> | void;
  };
  User: {
    PushSubscription: {
      optedIn: boolean;
      optIn(): Promise<void> | void;
    };
  };
};

type OneSignalDeferredCallback = (OneSignal: OneSignalSdk) => Promise<void> | void;

type OneSignalNotificationClickEvent = {
  preventDefault?: () => void;
  notification?: any;
  [key: string]: any;
};

declare global {
  interface Window {
    OneSignalDeferred?: OneSignalDeferredCallback[];
  }
}

@Injectable({
  providedIn: 'root',
})
export class OneSignalPushService {
  private initStarted = false;
  private initSucceeded = false;
  private clickListenerRegistered = false;
  private loggedInUserId: string | null = null;
  private lastRedirectTime = 0;
  private lastRedirectRoute: string | null = null;

  constructor(
    private router: Router,
    private ngZone: NgZone,
    private tokenService: TokenService,
    private breadcrumbService: BreadcrumbService,
    private sharedDataService: SharedDataService,
    private messagesService: MessagesService
  ) {
    this.listenForServiceWorkerNavigation();
  }

  init(): void {
    if (!this.canUseOneSignal() || this.initStarted) {
      return;
    }

    this.initStarted = true;
    this.withOneSignal(async (OneSignal) => {
      await OneSignal.init({
        appId: environment.oneSignalAppId,
        serviceWorkerPath: 'OneSignalSDKWorker.js',
        serviceWorkerParam: { scope: '/' },
        notificationClickHandlerMatch: 'origin',
        notificationClickHandlerAction: 'focus',
        autoResubscribe: true,
        promptOptions: {
          slidedown: {
            prompts: [
              {
                type: 'push',
                autoPrompt: true,
                delay: {
                  pageViews: 1,
                  timeDelay: 5,
                },
              },
            ],
          },
        },
      });

      this.initSucceeded = true;
      this.registerNotificationClickListener(OneSignal);

      if (this.loggedInUserId) {
        await OneSignal.login(this.loggedInUserId);
        this.promptForPush();
      }
    });
  }

  login(userId: string | null | undefined): void {
    if (!userId || !this.canUseOneSignal()) {
      return;
    }

    this.loggedInUserId = userId;
    this.init();

    if (this.initSucceeded) {
      this.withOneSignal(async (OneSignal) => {
        await OneSignal.login(userId);
        this.promptForPush();
      });
    }
  }

  logout(): void {
    if (!this.canUseOneSignal() || !this.initSucceeded) {
      this.loggedInUserId = null;
      return;
    }

    this.loggedInUserId = null;
    this.withOneSignal(async (OneSignal) => {
      await OneSignal.logout();
    });
  }

  promptForPush(): void {
    if (!this.canUseOneSignal()) {
      return;
    }

    this.init();
    this.withOneSignal(async (OneSignal) => {
      if (!OneSignal.Notifications.isPushSupported()) {
        return;
      }

      if (OneSignal.Notifications.permission) {
        await OneSignal.User.PushSubscription.optIn();
        return;
      }

      await OneSignal.Slidedown.promptPush({ force: true });
    });
  }

  private withOneSignal(callback: OneSignalDeferredCallback): void {
    window.OneSignalDeferred = window.OneSignalDeferred || [];
    window.OneSignalDeferred.push(async (OneSignal) => {
      try {
        await callback(OneSignal);
      } catch (error) {
        this.initStarted = false;
        this.initSucceeded = false;
        console.error('OneSignal push notification error', error);
      }
    });
  }

  private registerNotificationClickListener(OneSignal: OneSignalSdk): void {
    if (this.clickListenerRegistered) {
      return;
    }

    this.clickListenerRegistered = true;
    OneSignal.Notifications.addEventListener('click', (event: OneSignalNotificationClickEvent) => {
      const notif = event?.notification;
      const addData = notif?.additionalData;

      event.preventDefault?.();
      this.handleNotificationRedirect(addData ?? notif?.data ?? event);
    });
  }

  private listenForServiceWorkerNavigation(): void {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
      return;
    }

    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data?.type !== 'ONESIGNAL_NOTIFICATION_ROUTE') {
        return;
      }

      this.handleNotificationRedirect(event.data);
    });
  }

  private handleNotificationRedirect(data: any): void {
    const token = this.tokenService.getToken();
    const isLoggedIn = Boolean(token);

    const customData = data?.additionalData ?? data?.custom?.a ?? data?.custom?.additionalData ?? data;
    const type = customData?.type ?? customData?.notificationType;
    const typeId = customData?.typeId ?? customData?.chatRoomId ?? customData?.id;
    const explicitRoute =
      data?.route ??
      data?.additionalData?.route ??
      data?.custom?.a?.route ??
      data?.custom?.additionalData?.route;

    const targetRoute = this.resolveTargetRoute(type, typeId, explicitRoute, customData);

    const now = Date.now();
    if (this.lastRedirectRoute === targetRoute && (now - this.lastRedirectTime) < 800) {
      return;
    }
    this.lastRedirectRoute = targetRoute;
    this.lastRedirectTime = now;

    this.ngZone.run(() => {
      try {
        if (targetRoute === '/chat' || targetRoute.startsWith('/chat')) {
          this.sharedDataService.activeTab = '/chat';
          this.breadcrumbService.updateBreadcrumbs([
            { label: 'Home', url: '/dashboard' },
            { label: 'Chat', url: '/chat' },
          ]);

          const normalizedType = String(type ?? '').toUpperCase();
          const roomId = customData?.roomId ?? customData?.chatRoomId ?? customData?.roomID ?? (normalizedType === 'CHAT' ? typeId : null);
          const staffId = customData?.staffId ?? customData?.senderId ?? customData?.userId;
          const staffName = customData?.staffName ?? customData?.name;

          this.messagesService.requestOpenChat({
            roomId: roomId ? String(roomId) : null,
            staffId: staffId ? String(staffId) : null,
            staffName: staffName ? String(staffName) : null,
          });

          if (!isLoggedIn) {
            this.tokenService.setRedirectUrl('/chat');
            void this.router.navigate(['/login']);
            return;
          }

          void this.router.navigate(['/chat'], {
            state: {
              roomId: roomId ? String(roomId) : null,
              chatRoomId: roomId ? String(roomId) : null,
              staffId: staffId ? String(staffId) : null,
              staffName: staffName ? String(staffName) : null,
            }
          });
          return;
        }

        if (targetRoute.startsWith('/notification-detail')) {
          this.sharedDataService.activeTab = '/notification-detail';
          this.breadcrumbService.updateBreadcrumbs([
            { label: 'Home', url: '/dashboard' },
            { label: 'Notifications', url: '/notification-detail' },
          ]);
        }

        if (!isLoggedIn) {
          this.tokenService.setRedirectUrl(targetRoute);
          void this.router.navigate(['/login']);
          return;
        }

        void this.router.navigateByUrl(targetRoute);
      } catch (error) {
        console.error('[OneSignal Service] Failed to redirect from notification:', error);
        if (isLoggedIn) {
          void this.router.navigate(['/notification-detail']);
        } else {
          void this.router.navigate(['/login']);
        }
      }
    });
  }

  private resolveTargetRoute(type?: any, typeId?: any, explicitRoute?: any, customData?: any): string {
    const normalizedType = String(type ?? '').toUpperCase();
    if (normalizedType === 'CHAT' || normalizedType === 'CHAT_MESSAGE' || normalizedType === 'MESSAGE' || customData?.chatRoomId || customData?.roomId) {
      return '/chat';
    }

    const safeRoute = this.getSafeRoute(explicitRoute);
    if (safeRoute) {
      if (safeRoute.startsWith('/messages') || safeRoute.startsWith('/chat')) {
        return '/chat';
      }
      return safeRoute;
    }

    if (typeId) {
      return `/notification-detail/${encodeURIComponent(typeId)}`;
    }

    return '/notification-detail';
  }

  private getSafeRoute(route: unknown): string | null {
    if (typeof route !== 'string' || !route.startsWith('/') || route.startsWith('//')) {
      return null;
    }

    return route;
  }

  private canUseOneSignal(): boolean {
    return (
      typeof window !== 'undefined' &&
      typeof navigator !== 'undefined' &&
      'serviceWorker' in navigator &&
      this.isAllowedOrigin() &&
      Boolean(environment.oneSignalAppId)
    );
  }

  private isAllowedOrigin(): boolean {
    const allowedOrigins = environment.oneSignalAllowedOrigins ?? [];
    return allowedOrigins.length === 0 || allowedOrigins.includes(window.location.origin);
  }
}
